import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';

import { useIsElementVisible } from 'af-root/hooks/useIsElementVisible';
import type { OnMoreDataEvent } from 'af-root/hooks/useLoadMore';
import { useLoadMore } from 'af-root/hooks/useLoadMore';
import { useSelection } from 'af-root/hooks/useSelection';

import type { W_Material_EditBulk_RM_CreateSingle, W_Material_EditBulk_RM_EditSingle } from 'ab-api/web/material/editBulk';
import type { W_Material_FindForCompany_VM } from 'ab-api/web/material/findForCompany/viewModel';
import type { W_Material_FindForCompany_VM_Single } from 'ab-api/web/material/findForCompany/viewModel';
import type { W_Material_FindGroups_VM_Single } from 'ab-api/web/material/findGroups';

import { getId, getToLookupByKey, toLookupById } from '@acceligentllc/shared/utils/array';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';

import CLIENT from 'af-constants/routes/client';

import { Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';

import * as MaterialActions from 'af-actions/material';
import * as MaterialCostActions from 'af-actions/materialCost';

import BulkRateUpdateModal from './BulkRateUpdateModal';
import CompanyRatesTable from './CompanyRatesTable';
import { toCSVData } from './helpers';
import MaterialBulkControls from './MaterialBulkControls';
import type { MaterialEditFM, MaterialsFormInput, MaterialsFormOutput } from './MaterialsTableForm';
import MaterialsTableForm, { createMaterialsForm, createNewMaterial, MaterialEditFMSchema, MaterialFormSchema, materialVMtoFMInput } from './MaterialsTableForm';
import RateHistoryModal from './RateHistoryModal';
import styles from './styles.module.scss';
import TableLegend from './TableLegend';
import type { TableFilterSelection } from '../../../../Shared/Materials/TableFilters';
import TableFilters from '../../../../Shared/Materials/TableFilters';

interface MaterialDeleteInfo {
	materialId: number;
	displayName: string;
	index: number;
}

type Props = ConnectedProps<typeof connector>;

const DELETE_CONFIRMATION_BODY = (
	<>
		If deleted, the material will no longer be assignable. The material will get deleted once Save Changes is pressed.
		<br />
		<br />
		<b>This action cannot be undone.</b>
	</>
);

const BULK_DELETE_CONFIRMATION_BODY = (
	<>
		If deleted, the selected materials will no longer be assignable.
		<br />
		<br />
		<b>This action cannot be undone.</b>
	</>
);

function mapTags(tags: string) {
	return tags.split(',').reduce<string[]>((_acc, _tag) => {
		const tag = _tag.trim();
		if (!!tag.length) {
			_acc.push(tag);
		}
		return _acc;
	}, []);
}

function checkHasMoreMaterials(res: W_Material_FindForCompany_VM) {
	return res.hasMore;
}

const MaterialList: React.FC<Props> = (props) => {
	const {
		companyName,
		editMaterials,
		findCompanyMaterials,
		removeMaterialsBulk,
		findMaterialGroups,
		setDefaultOnJobBulk,
		removeScheduledCostForMaterial,
	} = props;

	const bottomElementRef = React.useRef<HTMLDivElement>(null);

	const [materials, setMaterials] = React.useState<W_Material_FindForCompany_VM_Single[]>([]);
	const [tableFilters, setTableFilters] = React.useState<TableFilterSelection>({ group: null, search: '' });
	const [materialForDelete, setMaterialForDelete] = React.useState<Nullable<MaterialDeleteInfo>>(null);
	const [showBulkRateEditModal, setShowBulkRateEditModal] = React.useState(false);
	const [showBulkDeleteModal, setShowBulkDeleteModal] = React.useState(false);
	const [editMode, setEditMode] = React.useState(false);
	const [materialForRateModal, setMaterialForRateModal] = React.useState<Nullable<MaterialEditFM>>(null);
	const [groups, setGroups] = React.useState<W_Material_FindGroups_VM_Single[]>([]);

	const materialIds = React.useMemo(() => {
		return materials.map(getId);
	}, [materials]);

	const {
		clearAll: clearSelectedMaterials,
		selected: selectedMaterials,
		toggleItem: toggleSelectedMaterial,
		selectAll: selectAllMaterials,
		selectedCount: selectedMaterialCount,
		totalCount: totalMaterialCount,
	} = useSelection(materialIds);

	const materialMap = React.useMemo(() => {
		return materials.reduce<Record<string, W_Material_FindForCompany_VM_Single>>(toLookupById, {});
	}, [materials]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const form = useForm<MaterialsFormInput, any, MaterialsFormOutput>({
		resolver: zodResolver(MaterialFormSchema),
		values: createMaterialsForm(materials),
		mode: 'onBlur',
		reValidateMode: 'onChange',
	});

	const materialsFieldArray = useFieldArray({
		control: form.control,
		keyName: 'id',
		name: 'materials',
	});

	const materialsForDeleteFieldArray = useFieldArray({
		control: form.control,
		keyName: 'materialId',
		name: 'materialsForDelete',
	});

	const navigate = useNavigate();
	const { state: { orgAlias } } = useLocation();

	const isScrolledToTheBottom = useIsElementVisible(bottomElementRef);

	const loadGroups = React.useCallback(async () => {
		const loaded = await findMaterialGroups();
		setGroups(loaded.groups);
	}, [findMaterialGroups]);

	const fetchMaterials = React.useMemo(() => {
		return (page: number) => findCompanyMaterials({ group: tableFilters.group ?? '', search: tableFilters.search, page });
	}, [findCompanyMaterials, tableFilters.group, tableFilters.search]);

	const onMoreMaterials = React.useCallback((event: OnMoreDataEvent<W_Material_FindForCompany_VM>) => {
		const { shouldReset, data: { materials: _materials } } = event;
		setMaterials((_currentMaterials) => {
			if (shouldReset) {
				return _materials;
			}
			return [..._currentMaterials, ..._materials];
		});
		if (shouldReset) {
			clearSelectedMaterials();
		}
	}, [clearSelectedMaterials]);

	const { load: fetchMoreMaterials, reset: resetLoadMore, isLoading } = useLoadMore(
		isScrolledToTheBottom && !editMode,
		fetchMaterials,
		checkHasMoreMaterials,
		onMoreMaterials
	);

	React.useEffect(() => {
		fetchMoreMaterials();
	}, [fetchMoreMaterials]);

	const closeDeleteModal = React.useCallback(() => {
		setMaterialForDelete(null);
	}, []);

	const deleteMaterial = React.useCallback(async () => {
		if (!materialForDelete) {
			return;
		}
		const { index, materialId } = materialForDelete;
		materialsFieldArray.remove(index);
		materialsForDeleteFieldArray.append({ materialId: materialId });
	}, [materialForDelete, materialsFieldArray, materialsForDeleteFieldArray]);

	const handleMaterialRemove = React.useCallback((index: number) => {
		const formMaterial = form.getValues('materials')[index];
		if (formMaterial.type === 'new') {
			materialsFieldArray.remove(index);
		} else {
			const { materialId, displayName } = formMaterial;
			setMaterialForDelete({ materialId, displayName, index });
		}
	}, [form, materialsFieldArray]);

	const closeMaterialRateHistoryModal = React.useCallback(() => {
		setMaterialForRateModal(null);
	}, []);

	const handleCSVUploadClick = React.useCallback(() => {
		navigate(CLIENT.COMPANY.RESOURCES.MATERIAL.BULK_UPLOAD(orgAlias, companyName));
	}, [companyName, navigate, orgAlias]);

	const handleNewMaterialClick = React.useCallback(() => {
		materialsFieldArray.prepend(createNewMaterial());
	}, [materialsFieldArray]);

	const selectedMaterialIds = React.useMemo(() => {
		return Object.keys(selectedMaterials).map(Number);
	}, [selectedMaterials]);

	const bulkControlsAreVisible = !!selectedMaterialIds.length;

	const handleBulkEditRates = React.useCallback(() => {
		setShowBulkRateEditModal(true);
	}, []);

	const closeBulkEditRatesModal = React.useCallback(() => {
		setShowBulkRateEditModal(false);
	}, []);

	const handleBulkDelete = React.useCallback(() => {
		setShowBulkDeleteModal(true);
	}, []);

	const closeBulkDeleteModal = React.useCallback(() => {
		setShowBulkDeleteModal(false);
	}, []);

	const bulkDeleteMaterials = React.useCallback(async () => {
		await removeMaterialsBulk({ materialIds: selectedMaterialIds });
		setMaterials((_materials) => _materials.filter((_mat) => !selectedMaterials[_mat.id]));
		clearSelectedMaterials();
	}, [clearSelectedMaterials, removeMaterialsBulk, selectedMaterialIds, selectedMaterials]);

	const handleEditTableClick = React.useCallback(() => {
		setEditMode(true);
		clearSelectedMaterials();
	}, [clearSelectedMaterials]);

	const leaveEditMode = React.useCallback(() => {
		setEditMode(false);
	}, []);

	const removeScheduledCost = React.useCallback(async (materialId: number) => {
		const material = materialMap[materialId];
		if (!material) {
			return;
		}
		const { futureRate } = material;
		if (!futureRate) {
			return;
		}
		setMaterials((_current) => {
			const next = [..._current];
			const index = _current.findIndex((_material) => _material.id === materialId);
			const nextMaterial = { ...next[index], futureRate: null };
			next[index] = nextMaterial;
			return next;
		});
		await removeScheduledCostForMaterial(materialId);
	}, [materialMap, removeScheduledCostForMaterial]);

	const submitData = React.useCallback(async (data: MaterialsFormOutput) => {
		const materialsToUpdate: W_Material_EditBulk_RM_EditSingle[] = [];
		const materialsToCreate: W_Material_EditBulk_RM_CreateSingle[] = [];

		const initialMaterials = materials.map(materialVMtoFMInput).map((_mat) => MaterialEditFMSchema.parse(_mat));
		const initialMaterialMap = initialMaterials.reduce<Record<string, MaterialEditFM>>(getToLookupByKey((_mat) => _mat.materialId), {});

		for (const material of data.materials) {
			if (material.type === 'new') {
				const { group1, group2, group3, tags, currentRate: { baseCost, shippingMarkup, salesMarkup, tax }, ...restFM } = material;
				materialsToCreate.push({
					...restFM,
					baseCost,
					shippingMarkup,
					salesMarkup,
					tax,
					materialGroups: [group1, group2, group3].filter((_g): _g is string => _g !== null),
					tags: mapTags(tags),
				});
			} else {
				const { group1, group2, group3, tags, futureRate, currentRate, materialId, ...restMaterial } = material;
				const keys = Object.keys(restMaterial) as (keyof typeof restMaterial)[];
				const rm: W_Material_EditBulk_RM_EditSingle = {
					id: materialId,
				};
				const originalMaterial = initialMaterialMap[materialId];
				for (const key of keys) {
					if (key !== 'id' && material[key] !== originalMaterial[key]) {
						rm[key] = material[key];
					}
				}
				if (
					group1 !== originalMaterial.group1
					|| group2 !== originalMaterial.group2
					|| group3 !== originalMaterial.group3
				) {
					rm.materialGroups = [group1, group2, group3].filter((_g): _g is string => _g !== null);
				}
				if (tags !== originalMaterial.tags) {
					rm.tags = mapTags(tags);
				}
				if (futureRate && originalMaterial.futureRate) {
					if (futureRate.baseCost !== originalMaterial.futureRate.baseCost) {
						rm.baseCost = futureRate.baseCost;
					}
					if (futureRate.shippingMarkup !== originalMaterial.futureRate.shippingMarkup) {
						rm.shippingMarkup = futureRate.shippingMarkup;
					}
					if (futureRate.salesMarkup !== originalMaterial.futureRate.salesMarkup) {
						rm.salesMarkup = futureRate.salesMarkup;
					}
					if (futureRate.tax !== originalMaterial.futureRate.tax) {
						rm.tax = futureRate.tax;
					}
				} else {
					if (currentRate.baseCost !== originalMaterial.currentRate.baseCost) {
						rm.baseCost = currentRate.baseCost;
					}
					if (currentRate.shippingMarkup !== originalMaterial.currentRate.shippingMarkup) {
						rm.shippingMarkup = currentRate.shippingMarkup;
					}
					if (currentRate.salesMarkup !== originalMaterial.currentRate.salesMarkup) {
						rm.salesMarkup = currentRate.salesMarkup;
					}
					if (currentRate.tax !== originalMaterial.currentRate.tax) {
						rm.tax = currentRate.tax;
					}
				}
				if (Object.keys(rm).length > 1) {
					materialsToUpdate.push(rm);
				}
			}
		}

		await editMaterials({ materials: materialsToUpdate, toCreate: materialsToCreate, toDelete: data.materialsForDelete.map((_mat) => _mat.materialId) });
		resetLoadMore();
	}, [editMaterials, materials, resetLoadMore]);

	const handleMarkAsJobDefault = React.useCallback(async () => {
		if (!selectedMaterialIds.length) {
			return;
		}
		await setDefaultOnJobBulk({ addDefaultOnJobIds: selectedMaterialIds, removeDefaultOnJobIds: [] });
		resetLoadMore();
	}, [resetLoadMore, selectedMaterialIds, setDefaultOnJobBulk]);

	const handleRemoveAsJobDefault = React.useCallback(async () => {
		if (!selectedMaterialIds.length) {
			return;
		}
		await setDefaultOnJobBulk({ addDefaultOnJobIds: [], removeDefaultOnJobIds: selectedMaterialIds });
		resetLoadMore();
	}, [resetLoadMore, selectedMaterialIds, setDefaultOnJobBulk]);

	const handleSubmit = React.useMemo(() => form.handleSubmit(submitData, (error) => console.error(error)), [form, submitData]);

	const handleSaveChangesClick = React.useCallback(async () => {
		handleSubmit();
		leaveEditMode();
	}, [leaveEditMode, handleSubmit]);

	const handleCancelEditsClick = React.useCallback(async () => {
		form.reset();
		leaveEditMode();
	}, [form, leaveEditMode]);

	const handleCSVExportClick = React.useCallback(() => {
		const data = toCSVData(materials);
		downloadCSV(data, 'materials.csv');
	}, [materials]);

	return (
		<>
			<CompanyRatesTable />
			<div className={`form-box ${styles.formBox}`}>
				<div className={styles.tableHeader}>
					<TableFilters
						className={styles.filters}
						disabled={editMode}
						filters={tableFilters}
						groups={groups}
						onChange={setTableFilters}
						onLoadRequest={loadGroups}
					/>
					<div className={styles.actions}>
						{!editMode ? (
							<>
								<Button
									customStyleClass={styles.action}
									icon="ActionDownload"
									label="CSV Export"
									onClick={handleCSVExportClick}
									style="link"
								/>
								<Button
									customStyleClass={styles.action}
									icon="ActionImport"
									label="CSV Upload"
									onClick={handleCSVUploadClick}
									style="link"
								/>
								<Button
									customStyleClass={styles.action}
									label="Edit Table"
									onClick={handleEditTableClick}
									style="primary"
								/>
							</>
						) : (
							<>
								<Button
									customStyleClass={styles.action}
									label="Cancel Edits"
									onClick={handleCancelEditsClick}
									style="secondary"
								/>
								<Button
									customStyleClass={styles.action}
									label="Add New Material"
									onClick={handleNewMaterialClick}
									style="secondary"
								/>
								<Button
									customStyleClass={styles.action}
									disabled={!form.formState.isValid}
									label="Save Changes"
									onClick={handleSaveChangesClick}
									style="primary"
								/>
							</>
						)}
					</div>
				</div>
				<TableLegend />
				<MaterialBulkControls
					isVisible={bulkControlsAreVisible}
					onDelete={handleBulkDelete}
					onEditRates={handleBulkEditRates}
					onMarkAsDefaultOnJob={handleMarkAsJobDefault}
					onRemoveAsDefaultOnJob={handleRemoveAsJobDefault}
				/>
				<MaterialsTableForm
					clearSelectedMaterials={clearSelectedMaterials}
					fields={materialsFieldArray.fields}
					form={form}
					onDelete={handleMaterialRemove}
					onMaterialCheck={toggleSelectedMaterial}
					onRatePreview={setMaterialForRateModal}
					readonly={!editMode}
					removeFutureCost={removeScheduledCost}
					selectAllMaterials={selectAllMaterials}
					selectedMaterialCount={selectedMaterialCount}
					selectedMaterials={selectedMaterials}
					totalMaterialCount={totalMaterialCount}

				/>
			</div>
			<ConfirmationModal
				body={DELETE_CONFIRMATION_BODY}
				closeModal={closeDeleteModal}
				confirmAction={deleteMaterial}
				confirmText="Delete Material"
				showModal={!!materialForDelete}
				size="md"
				title={`Are you sure you want to delete Material (${materialForDelete?.displayName})?`}
			/>
			<BulkRateUpdateModal
				close={closeBulkEditRatesModal}
				materialIds={selectedMaterialIds}
				onSubmit={resetLoadMore}
				showModal={showBulkRateEditModal}
			/>
			<ConfirmationModal
				body={BULK_DELETE_CONFIRMATION_BODY}
				closeModal={closeBulkDeleteModal}
				confirmAction={bulkDeleteMaterials}
				confirmText="Delete Materials"
				showModal={showBulkDeleteModal}
				size="md"
				title="Delete Selected Materials?"
			/>
			<RateHistoryModal
				close={closeMaterialRateHistoryModal}
				material={materialForRateModal}
			/>
			{isLoading && <div className={styles.loadingText}>Loading...</div>}
			<div className={styles.bottomElement} ref={bottomElementRef} />
		</>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData, userData } = state.user;

	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		editMaterials: MaterialActions.editMaterialBulk,
		findCompanyMaterials: MaterialActions.findCompanyMaterials,
		removeMaterialsBulk: MaterialActions.removeMaterialsBulk,
		findMaterialGroups: MaterialActions.findMaterialGroups,
		setDefaultOnJobBulk: MaterialActions.setDefaultOnJobBulk,
		removeScheduledCostForMaterial: MaterialCostActions.removeScheduledCostForMaterial,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(MaterialList);
